import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const IntegrationSettings = ({ onChange }) => {
  const [integrations, setIntegrations] = useState([
    {
      id: 'stripe',
      name: 'Stripe',
      description: 'Payment processing and invoicing',
      icon: 'CreditCard',
      connected: true,
      status: 'active',
      lastSync: '2026-01-30 10:00 AM'
    },
    {
      id: 'plaid',
      name: 'Plaid',
      description: 'Bank account connections',
      icon: 'Landmark',
      connected: true,
      status: 'active',
      lastSync: '2026-01-30 09:30 AM'
    },
    {
      id: 'quickbooks',
      name: 'QuickBooks',
      description: 'Accounting data sync',
      icon: 'RefreshCw',
      connected: false,
      status: 'disconnected',
      lastSync: 'Never'
    },
    {
      id: 'salesforce',
      name: 'Salesforce',
      description: 'CRM integration',
      icon: 'Users',
      connected: false,
      status: 'disconnected',
      lastSync: 'Never'
    }
  ]);

  const [apiKeys, setApiKeys] = useState({
    publicKey: 'pk_live_51H***************',
    secretKey: '••••••••••••••••••••••••',
    webhookSecret: 'whsec_••••••••••••••••••'
  });

  const [showApiKey, setShowApiKey] = useState(false);

  const handleConnect = (integrationId) => {
    setIntegrations(integrations?.map(int => 
      int?.id === integrationId 
        ? { ...int, connected: true, status: 'active', lastSync: new Date()?.toLocaleString() }
        : int
    ));
    onChange?.();
  };

  const handleDisconnect = (integrationId) => {
    if (window.confirm('Are you sure you want to disconnect this integration?')) {
      setIntegrations(integrations?.map(int => 
        int?.id === integrationId 
          ? { ...int, connected: false, status: 'disconnected', lastSync: 'Never' }
          : int
      ));
      onChange?.();
    }
  };

  const handleSync = (integrationId) => {
    console.log('Syncing integration:', integrationId);
    setIntegrations(integrations?.map(int => 
      int?.id === integrationId 
        ? { ...int, lastSync: new Date()?.toLocaleString() }
        : int
    ));
  };

  return (
    <div className="space-y-8">
      {/* API Keys Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">API Keys</h3>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Public Key</label>
            <div className="flex gap-2">
              <Input
                value={apiKeys?.publicKey}
                readOnly
                className="flex-1"
              />
              <Button
                variant="outline"
                iconName="Copy"
                onClick={() => navigator.clipboard?.writeText(apiKeys?.publicKey)}
              >
                Copy
              </Button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Secret Key</label>
            <div className="flex gap-2">
              <Input
                type={showApiKey ? 'text' : 'password'}
                value={apiKeys?.secretKey}
                readOnly
                className="flex-1"
              />
              <Button
                variant="outline"
                iconName={showApiKey ? 'EyeOff' : 'Eye'}
                onClick={() => setShowApiKey(!showApiKey)}
              />
            </div>
          </div>

          <div className="pt-2">
            <Button variant="outline" iconName="RotateCcw" size="sm">
              Regenerate Keys
            </Button>
          </div>
        </div>
      </div>
      {/* Third-Party Integrations */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Third-Party Integrations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {integrations?.map((integration) => (
            <div
              key={integration?.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={integration?.icon} size={24} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{integration?.name}</h4>
                    <p className="text-sm text-gray-600">{integration?.description}</p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    integration?.connected
                      ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                  }`}
                >
                  {integration?.status}
                </span>
              </div>

              <div className="text-sm text-gray-600 mb-3">
                Last sync: {integration?.lastSync}
              </div>

              <div className="flex gap-2">
                {integration?.connected ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="RefreshCw"
                      onClick={() => handleSync(integration?.id)}
                      fullWidth
                    >
                      Sync Now
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Unplug"
                      onClick={() => handleDisconnect(integration?.id)}
                    >
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    iconName="Plug"
                    onClick={() => handleConnect(integration?.id)}
                    fullWidth
                  >
                    Connect
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Data Sync Settings */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Synchronization</h3>
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Auto-Sync</h4>
              <p className="text-sm text-gray-600">Automatically sync data every hour</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Sync Notifications</h4>
              <p className="text-sm text-gray-600">Get notified when sync completes</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationSettings;